import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ClockAlert, CheckCircle2, XCircle, X } from "lucide-react";

/**
 * Timesheets — weekly grid view. One row per week (Mon left → Sun
 * right), each day cell stacks the timesheet entries logged that day.
 *
 * Filters: status pills + employee dropdown (defaults to "All
 * employees"). Both filter the already-loaded data client-side; no
 * extra API calls when toggling. Backed by
 * `GET /api/integrations/keypay/clients/{cid}/timesheets`.
 */
// View windows:
//   "recent" (default) — past 2 weeks + current week, Monday-aligned.
//   "fy"               — 1 Jul → 30 Jun (AU financial year).
//   "cy"               — 1 Jan → 31 Dec (calendar year).
function windowFor(viewMode, anchor = new Date()) {
  const y = anchor.getFullYear();
  if (viewMode === "cy") {
    return { from: `${y}-01-01`, to: `${y}-12-31`, label: `CY ${y}` };
  }
  if (viewMode === "fy") {
    // FY: if we're in Jul-Dec, FY started this year; otherwise last year.
    const m = anchor.getMonth(); // 0-indexed
    const fyStart = m >= 6 ? y : y - 1;
    return {
      from: `${fyStart}-07-01`,
      to: `${fyStart + 1}-06-30`,
      label: `FY ${String(fyStart).slice(2)}/${String(fyStart + 1).slice(2)}`,
    };
  }
  // "recent" — 3 weeks ending Sunday of the current week.
  const today = new Date(anchor);
  const dow = (today.getDay() + 6) % 7; // Mon-first: Mon=0..Sun=6
  const monday = new Date(today);
  monday.setDate(today.getDate() - dow);
  const start = new Date(monday);
  start.setDate(monday.getDate() - 14); // 2 weeks back
  const end = new Date(monday);
  end.setDate(monday.getDate() + 6); // Sunday of current week
  const iso = (d) => {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };
  return { from: iso(start), to: iso(end), label: "Last 3 weeks" };
}

export default function KeyPayTimesheetsPage() {
  const { clientId } = useParams();
  const [status, setStatus] = useState(""); // "" = all
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [viewMode, setViewMode] = useState("recent"); // "recent" | "fy" | "cy"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const yearWin = useMemo(() => windowFor(viewMode), [viewMode]);

  // Selection & bulk-action state ────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const reloadTimesheets = useCallback(() => {
    const params = { from_date: yearWin.from, to_date: yearWin.to };
    if (status) params.status = status;
    setLoading(true);
    return api.get(`/integrations/keypay/clients/${clientId}/timesheets`, { params })
      .then((r) => { setData(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load timesheets"))
      .finally(() => { setLoading(false); });
  }, [clientId, status, yearWin.from, yearWin.to]);

  const runBulkAction = useCallback(async (kind) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const path = kind === "approve" ? "approve" : "reject";
      const body = { timesheet_ids: ids };
      if (kind === "reject" && rejectReason.trim()) body.reason = rejectReason.trim();
      const { data: res } = await api.post(
        `/integrations/keypay/clients/${clientId}/timesheets/${path}`, body);
      if (res.failed > 0) {
        toast.warning(`${kind === "approve" ? "Approved" : "Rejected"} ${res.succeeded}/${res.requested} · ${res.failed} failed`);
      } else {
        toast.success(`${kind === "approve" ? "Approved" : "Rejected"} ${res.succeeded} timesheet(s)`);
      }
      clearSelection();
      setRejectOpen(false);
      setRejectReason("");
      await reloadTimesheets();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${kind} failed`);
    } finally { setBusy(false); }
  }, [selectedIds, rejectReason, clientId, clearSelection, reloadTimesheets]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { from_date: yearWin.from, to_date: yearWin.to };
    if (status) params.status = status;
    api.get(`/integrations/keypay/clients/${clientId}/timesheets`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load timesheets"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, status, yearWin.from, yearWin.to]);

  // When the filter/year window changes, drop selections that are no
  // longer visible (avoid submitting a stale id to KeyPay).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set();
      for (const g of data?.groups || []) {
        for (const e of g.entries || []) {
          if (e.id != null) visible.add(e.id);
        }
      }
      const next = new Set();
      prev.forEach((id) => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [data]);

  // Flatten every group → entry rows (one row per timesheet) so we can
  // pivot the data into a week × day grid below.
  const allEntries = useMemo(() => {
    const out = [];
    for (const g of data?.groups || []) {
      for (const e of g.entries || []) {
        out.push({ ...e, employee_name: g.employee_name, employee_id: g.employee_id });
      }
    }
    return out;
  }, [data]);

  // Employee options for the dropdown — only people who actually have
  // entries in the loaded window. Sorted alphabetically.
  const employeeOptions = useMemo(() => {
    const seen = new Map();
    for (const e of allEntries) {
      if (e.employee_id == null) continue;
      if (!seen.has(e.employee_id)) {
        seen.set(e.employee_id, {
          id: String(e.employee_id),
          name: e.employee_name || `Employee ${e.employee_id}`,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allEntries]);

  // Apply the employee dropdown.
  const filteredEntries = useMemo(() => {
    if (employeeFilter === "all") return allEntries;
    return allEntries.filter((e) => String(e.employee_id) === employeeFilter);
  }, [allEntries, employeeFilter]);

  // Pivot to {weekStartIso → {dayIso → entry[]}}. Weeks start Monday.
  // Use the selected window (FY / CY) for the date bounds so we always
  // render all 52-53 weeks of the year, even ones without entries.
  const weeks = useMemo(() => buildWeeks(filteredEntries, yearWin.from, yearWin.to),
                        [filteredEntries, yearWin.from, yearWin.to]);

  const totalHoursVisible = useMemo(
    () => filteredEntries.reduce((s, e) => s + (e.hours || 0), 0),
    [filteredEntries]);

  const showEmployeeNameInCell = employeeFilter === "all";

  return (
    <div className="p-6 max-w-[1400px]" data-testid="keypay-timesheets-page">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-ts-back">
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100 text-amber-700">
          <ClockAlert className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="label-overline">KEYPAY · TIMESHEETS · WEEKLY GRID · {yearWin.label}</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Timesheets</h1>
          {data && (
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
              {employeeFilter === "all"
                ? `${data.count} entries across ${data.employee_count} employee(s)`
                : `${filteredEntries.length} entries for ${employeeOptions.find((o) => o.id === employeeFilter)?.name || "selected"}`}
              <span className="text-zinc-400"> · {totalHoursVisible.toFixed(1)} h</span>
              <span className="text-zinc-400"> · {yearWin.from} → {yearWin.to}</span>
            </div>
          )}
        </div>

        {/* View-window toggle: 3 weeks default (past 2 + current), or
            full FY/CY. FY view shows July at top → June at bottom. */}
        <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5"
          data-testid="keypay-ts-year-toggle">
          {[
            { v: "recent", label: "3 Weeks" },
            { v: "fy", label: "Financial Year" },
            { v: "cy", label: "Calendar Year" },
          ].map((opt) => (
            <button key={opt.v}
              onClick={() => setViewMode(opt.v)}
              data-testid={`keypay-ts-year-${opt.v}`}
              className={`px-3 h-8 rounded-sm font-display font-bold text-[11px] uppercase tracking-wider transition ${
                viewMode === opt.v ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
              }`}>
              {opt.label}
            </button>
          ))}
        </div>

        {/* Employee dropdown — defaults to All */}
        <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
          <SelectTrigger className="rounded-sm h-9 w-[220px]"
            data-testid="keypay-ts-employee-select">
            <SelectValue placeholder="All employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="keypay-ts-employee-all">
              All employees
              {employeeOptions.length > 0 && (
                <span className="text-zinc-400 font-mono ml-1">· {employeeOptions.length}</span>
              )}
            </SelectItem>
            {employeeOptions.map((emp) => (
              <SelectItem key={emp.id} value={emp.id}
                data-testid={`keypay-ts-employee-${emp.id}`}>
                {emp.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 flex-wrap">
          {[
            { v: "", label: "All" },
            { v: "submitted", label: "Submitted" },
            { v: "approved", label: "Approved" },
            { v: "processed", label: "Processed" },
            { v: "rejected", label: "Rejected" },
          ].map((opt) => (
            <button key={opt.v || "all"}
              onClick={() => setStatus(opt.v)}
              data-testid={`keypay-ts-status-${opt.v || "all"}`}
              className={`px-3 h-9 rounded-sm border font-display font-bold text-xs uppercase tracking-wider transition ${
                status === opt.v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></CardContent></Card>
      ) : weeks.length === 0 ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-8 text-center text-sm italic text-zinc-500">
            No timesheets recorded in this status / employee filter for the loaded window.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Quick "select all submitted in view" bar — only shows when
              there ARE submitted entries in the filtered view. */}
          {(() => {
            const submittedIds = filteredEntries
              .filter((e) => e.status === "submitted" && e.id != null)
              .map((e) => e.id);
            if (submittedIds.length === 0) return null;
            const allSelected = submittedIds.every((id) => selectedIds.has(id));
            return (
              <div className="mb-2 flex items-center gap-2 text-[11px] text-zinc-500"
                data-testid="keypay-ts-select-all-bar">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (allSelected) {
                        submittedIds.forEach((id) => next.delete(id));
                      } else {
                        submittedIds.forEach((id) => next.add(id));
                      }
                      return next;
                    });
                  }}
                  className="underline hover:text-zinc-900"
                  data-testid="keypay-ts-select-all-submitted">
                  {allSelected ? "Deselect" : "Select"} all {submittedIds.length} submitted
                </button>
                {selectedIds.size > 0 && (
                  <>
                    <span className="text-zinc-300">·</span>
                    <button type="button" onClick={clearSelection}
                      className="underline hover:text-zinc-900"
                      data-testid="keypay-ts-clear-selection">
                      Clear selection ({selectedIds.size})
                    </button>
                  </>
                )}
              </div>
            );
          })()}

          <Card className="rounded-sm border-zinc-200" data-testid="keypay-ts-week-grid">
            <CardContent className="p-0">
              {/* Day-of-week header */}
              <div className="grid grid-cols-[110px_repeat(7,1fr)] border-b border-zinc-200 bg-zinc-50">
                <div className="px-2 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 border-r border-zinc-200">
                  Week
                </div>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 border-r border-zinc-100 last:border-r-0">
                    {d}
                  </div>
                ))}
              </div>
              {weeks.map((wk) => (
                <WeekRow key={wk.startIso} wk={wk}
                  clientId={clientId}
                  showEmployeeNameInCell={showEmployeeNameInCell}
                  selectedIds={selectedIds}
                  toggleSelect={toggleSelect} />
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {/* Sticky bulk-action bar — appears only when timesheets are
          selected. Approve fires directly; Reject opens a dialog to
          capture the reason (appended to hiddenComments). */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40
          bg-zinc-900 text-white rounded-sm shadow-2xl px-4 py-3
          flex items-center gap-3 border border-zinc-800"
          data-testid="keypay-ts-action-bar">
          <span className="text-sm font-display font-bold">
            {selectedIds.size} selected
          </span>
          <div className="h-4 w-px bg-zinc-700" />
          <Button size="sm" onClick={() => runBulkAction("approve")}
            disabled={busy}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white h-8"
            data-testid="keypay-ts-approve-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Approve
          </Button>
          <Button size="sm" onClick={() => setRejectOpen(true)}
            disabled={busy}
            className="rounded-sm bg-red-600 hover:bg-red-500 text-white h-8"
            data-testid="keypay-ts-reject-open-btn">
            <XCircle className="h-3.5 w-3.5 mr-1" />Reject
          </Button>
          <button onClick={clearSelection}
            className="text-zinc-400 hover:text-white ml-1"
            data-testid="keypay-ts-action-bar-close"
            title="Clear selection">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Reject-with-reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={(o) => { setRejectOpen(o); if (!o) setRejectReason(""); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="keypay-ts-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {selectedIds.size} timesheet(s)</DialogTitle>
            <DialogDescription>
              Optional reason — appended to the timesheet&apos;s hidden
              comments in KeyPay (visible to admins, not the employee)
              with your timestamp so the audit trail is clear.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            placeholder="e.g. Break not recorded correctly — please resubmit with the 30-min lunch break split out."
            maxLength={500}
            data-testid="keypay-ts-reject-reason-input" />
          <div className="text-[10px] text-zinc-400 text-right font-mono">
            {rejectReason.length}/500
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}
              className="rounded-sm">Cancel</Button>
            <Button onClick={() => runBulkAction("reject")}
              disabled={busy}
              className="rounded-sm bg-red-600 hover:bg-red-500 text-white"
              data-testid="keypay-ts-reject-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Reject {selectedIds.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WeekRow({ wk, clientId, showEmployeeNameInCell, selectedIds, toggleSelect }) {
  return (
    <div className="grid grid-cols-[110px_repeat(7,1fr)] border-b border-zinc-100 last:border-b-0"
      data-testid={`keypay-ts-week-${wk.startIso}`}>
      <div className="px-2 py-2 border-r border-zinc-200 bg-zinc-50/50">
        <div className="text-[11px] font-display font-bold text-zinc-900">{wk.label}</div>
        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
          {wk.totalHours.toFixed(1)} h
        </div>
        {wk.uniqueEmployees > 0 && showEmployeeNameInCell && (
          <div className="text-[9px] font-mono text-zinc-400 mt-0.5">
            {wk.uniqueEmployees} ppl
          </div>
        )}
      </div>
      {wk.days.map((day) => (
        <DayCell key={day.iso} day={day} clientId={clientId}
          showEmployeeNameInCell={showEmployeeNameInCell}
          selectedIds={selectedIds}
          toggleSelect={toggleSelect} />
      ))}
    </div>
  );
}

function DayCell({ day, clientId, showEmployeeNameInCell, selectedIds, toggleSelect }) {
  return (
    <div className={`min-h-[88px] border-r border-zinc-100 last:border-r-0 p-1.5 ${
      day.isToday ? "bg-amber-50/40" : day.isWeekend ? "bg-zinc-50/40" : ""
    }`}
    data-testid={`keypay-ts-day-${day.iso}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] font-mono ${day.isToday ? "font-bold text-zinc-900" : "text-zinc-500"}`}>
          {day.dayNum}
        </span>
        {day.totalHours > 0 && (
          <Badge className="rounded-sm text-[9px] bg-amber-100 text-amber-800 border-amber-200 font-mono px-1 py-0">
            {day.totalHours.toFixed(1)}h
          </Badge>
        )}
      </div>
      <div className="space-y-0.5">
        {day.entries.slice(0, 4).map((e) => {
          const submittable = e.status === "submitted";
          const checked = selectedIds.has(e.id);
          return (
            <div key={e.id} className="flex items-start gap-1">
              {submittable && (
                <input type="checkbox" checked={checked}
                  onChange={() => toggleSelect(e.id)}
                  className="mt-0.5 h-3 w-3 shrink-0 accent-emerald-600 cursor-pointer"
                  data-testid={`keypay-ts-entry-checkbox-${e.id}`}
                  title="Select for bulk approve/reject" />
              )}
              <Link
                to={`/integrations/keypay/clients/${clientId}/employees/${e.employee_id}`}
                className={`flex-1 block text-[10px] truncate rounded-sm px-1 py-0.5 hover:bg-zinc-100 ${
                  e.status === "approved" || e.status === "processed"
                    ? "bg-emerald-50 text-emerald-900"
                    : e.status === "submitted" ? "bg-amber-50 text-amber-900"
                    : e.status === "rejected" ? "bg-red-50 text-red-900"
                    : "bg-zinc-50 text-zinc-700"
                } ${checked ? "ring-1 ring-emerald-500" : ""}`}
                title={`${e.employee_name} · ${e.work_type || ""} · ${(e.hours || 0).toFixed(2)}h · ${e.status}`}>
                {showEmployeeNameInCell
                  ? <><span className="font-semibold">{(e.employee_name || "").split(" ")[0]}</span>{" "}· {e.hours.toFixed(1)}h</>
                  : <>{e.hours.toFixed(1)}h · {(e.work_type || "—").slice(0, 12)}</>
                }
              </Link>
            </div>
          );
        })}
        {day.entries.length > 4 && (
          <div className="text-[9px] text-zinc-500 px-1">+{day.entries.length - 4} more</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Week pivot — convert flat entries into a list of weeks descending
// from most recent → oldest. Weeks run Monday → Sunday.
// ─────────────────────────────────────────────────────────────────
function buildWeeks(entries, fromIso, toIso) {
  if (!entries) return [];
  // Determine the date range: bounds from the loaded data window if
  // available, otherwise from the entries themselves.
  const from = fromIso ? new Date(fromIso) : minDate(entries);
  const to = toIso ? new Date(toIso) : maxDate(entries);
  if (!from || !to) return [];

  // Anchor `from` to the Monday of its week.
  const start = new Date(from);
  const dow = (start.getDay() + 6) % 7; // Mon-first
  start.setDate(start.getDate() - dow);

  // Index entries by yyyy-mm-dd for O(1) lookup per cell.
  const byDay = {};
  for (const e of entries) {
    const k = (e.date || "").slice(0, 10);
    if (!k) continue;
    (byDay[k] = byDay[k] || []).push(e);
  }

  const today = isoLocal(new Date());
  const weeks = [];
  const cur = new Date(start);
  while (cur <= to) {
    const wkStart = new Date(cur);
    const days = [];
    let totalHours = 0;
    const employeeSet = new Set();
    for (let i = 0; i < 7; i++) {
      const d = new Date(wkStart);
      d.setDate(wkStart.getDate() + i);
      const dIso = isoLocal(d);
      const dayEntries = (byDay[dIso] || []).slice().sort((a, b) =>
        (a.employee_name || "").localeCompare(b.employee_name || "") ||
        (a.start || "").localeCompare(b.start || ""));
      const dayHours = dayEntries.reduce((s, x) => s + (x.hours || 0), 0);
      totalHours += dayHours;
      for (const e of dayEntries) if (e.employee_id != null) employeeSet.add(e.employee_id);
      days.push({
        iso: dIso,
        dayNum: d.getDate(),
        isToday: dIso === today,
        isWeekend: i >= 5,
        entries: dayEntries,
        totalHours: dayHours,
      });
    }
    weeks.push({
      startIso: isoLocal(wkStart),
      label: weekLabel(wkStart),
      days, totalHours,
      uniqueEmployees: employeeSet.size,
    });
    cur.setDate(cur.getDate() + 7);
  }
  // Chronological order: oldest week at TOP, newest at BOTTOM. For FY
  // this means July at top → June at bottom, matching payroll-ledger
  // convention. Show ALL weeks in the requested window (even zero-hour
  // ones) so admins can visually spot quiet weeks at a glance.
  return weeks;
}

function weekLabel(monday) {
  const end = new Date(monday);
  end.setDate(monday.getDate() + 6);
  return `${monday.getDate()} ${monday.toLocaleString(undefined, { month: "short" })}`;
}
function isoLocal(d) {
  // Local-tz YYYY-MM-DD (avoids the toISOString UTC shift breaking
  // weekday alignment for AU users).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function minDate(arr) {
  let m = null;
  for (const e of arr) {
    const d = e.date ? new Date(e.date) : null;
    if (d && (!m || d < m)) m = d;
  }
  return m;
}
function maxDate(arr) {
  let m = null;
  for (const e of arr) {
    const d = e.date ? new Date(e.date) : null;
    if (d && (!m || d > m)) m = d;
  }
  return m;
}
function withinWindow(iso, from, to) {
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return true;
}
